"use client";

import React, { useEffect, useRef } from "react";
import "./Diwali.css";

const PALETTES = [
  ["#ffd54f", "#ffb300", "#fff59d"],
  ["#ff5252", "#ff8a65", "#ffab40"],
  ["#f48fb1", "#ce93d8", "#fff176"],
  ["#80deea", "#a5d6a7", "#fff59d"],
  ["#ffffff", "#ffd54f", "#ff7043"],
];
const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];
const TAU = Math.PI * 2;

// Unit-vector explosion shapes
const SHAPES = {
  circle: (n) =>
    Array.from({ length: n }, (_, i) => [
      Math.cos((TAU * i) / n),
      Math.sin((TAU * i) / n),
      1,
    ]),
  ring: (n) => [
    ...SHAPES.circle(n),
    ...SHAPES.circle(Math.round(n * 0.6)).map(([x, y]) => [
      x * 0.55,
      y * 0.55,
      0.55,
    ]),
  ],
  star: (n) =>
    Array.from({ length: n }, (_, i) => {
      const a = (TAU * i) / n;
      const r = 0.55 + 0.45 * Math.abs(Math.cos(2.5 * a));
      return [Math.cos(a) * r, Math.sin(a) * r, r];
    }),
  heart: (n) =>
    Array.from({ length: n }, (_, i) => {
      const t = (TAU * i) / n;
      const x = 16 * Math.sin(t) ** 3;
      const y = -(
        13 * Math.cos(t) -
        5 * Math.cos(2 * t) -
        2 * Math.cos(3 * t) -
        Math.cos(4 * t)
      );
      return [x / 17, y / 17, 1];
    }),
  crackle: (n) =>
    Array.from({ length: n }, () => {
      const a = rand(0, TAU);
      const r = Math.sqrt(Math.random());
      return [Math.cos(a) * r, Math.sin(a) * r, r];
    }),
};
const SHAPE_KEYS = Object.keys(SHAPES);

// Persistent fixed fireworks overlay. It is mounted once at the Homepage level and its
// rAF loop is independent of scroll position (only paused while the tab is hidden).
export default function DiwaliHomeEffects() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const mobile = window.innerWidth < 768;
    const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2);
    const MAX_PARTICLES = mobile ? 420 : 950;
    const TRAIL = mobile ? 6 : 9; // length of each streak
    const particles = [];
    const rockets = [];
    let w = 0,
      h = 0,
      cw = 0,
      ch = 0,
      raf = 0,
      last = 0,
      paused = document.hidden;
    let nextLaunch = performance.now() + 1800;

    const resize = () => {
      const nw = window.innerWidth,
        nh = window.innerHeight;
      // ignore small height changes (mobile URL bar showing/hiding while scrolling)
      if (w && nw === cw && Math.abs(nh - ch) < 160) return;
      cw = nw;
      ch = nh;
      w = nw;
      h = nh;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const burst = (x, y, { small = false, bg = false } = {}) => {
      const pal = pick(PALETTES);
      const shape = SHAPES[pick(SHAPE_KEYS)];
      const base = small || mobile ? 40 : 80; // more spokes
      const vecs = shape(base + ((Math.random() * 20) | 0));
      const willow = Math.random() < 0.18;
      const speed = (small ? 3 : 4.2) * (mobile ? 0.75 : 1);
      const alpha = bg ? 0.55 : 1;
      const tailColor = pick(pal); // one tint per burst looks cleaner
      for (const [ux, uy, k] of vecs) {
        if (particles.length >= MAX_PARTICLES) break;
        const v = speed * rand(0.55, 1.1); // varied length spokes
        particles.push({
          x,
          y,
          vx: ux * v,
          vy: uy * v,
          life: 1,
          decay: willow
            ? rand(0.005, 0.009)
            : rand(0.009, 0.016) / (0.6 + 0.4 * k),
          g: willow ? 0.045 : 0.03,
          c: willow ? "#ffca28" : Math.random() < 0.35 ? "#fff4d6" : tailColor,
          w: bg ? 0.7 : rand(0.9, 1.6), // line width
          a: alpha,
          t: [], // trail history
        });
      }
    };

    const launchOne = () => {
      // stay mostly in the side margins so central content stays readable
      const x = mobile
        ? rand(0.06, 0.94) * w
        : Math.random() < 0.5
          ? rand(0.04, 0.22) * w
          : rand(0.78, 0.96) * w;
      rockets.push({
        x,
        y: h + 8,
        vx: rand(-0.7, 0.7),
        vy: -rand(mobile ? 6 : 7, mobile ? 8 : 10),
        ty: rand(0.14, 0.5) * h,
        ph: rand(0, TAU),
        wob: rand(0.1, 0.5),
        trail: [],
        c: pick(PALETTES)[0],
      });
    };

    const schedule = (t) => {
      if (Math.random() < 0.25) {
        launchOne();
        setTimeout(
          () => !paused && rockets.length < 3 && launchOne(),
          rand(250, 700),
        );
      } else if (Math.random() < 0.3) {
        // distant background burst
        burst(rand(0.05, 0.95) * w, rand(0.1, 0.45) * h, {
          small: true,
          bg: true,
        });
      } else {
        launchOne();
      }
      nextLaunch = t + rand(3000, 7000);
    };

    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (paused) return;
      const elapsed = t - last;
      if (elapsed < 30) return; // ~30fps cap
      const dt = Math.min(elapsed, 100) / 16.67;
      last = t;

      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";

      if (t > nextLaunch && rockets.length < 3) schedule(t);

      // ---- rockets (unchanged) ----
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.y += r.vy * dt;
        r.x += (r.vx + Math.sin(r.y * 0.04 + r.ph) * r.wob) * dt;
        r.trail.push(r.x, r.y);
        if (r.trail.length > 28) r.trail.splice(0, 2);
        const n = r.trail.length / 2;
        for (let k = 0; k < n; k++) {
          const f = k / n;
          ctx.globalAlpha = f * 0.8;
          ctx.fillStyle = f > 0.7 ? "#fff3c4" : "#ff9800";
          ctx.beginPath();
          ctx.arc(r.trail[k * 2], r.trail[k * 2 + 1], 0.6 + f * 1.8, 0, TAU);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2.4, 0, TAU);
        ctx.fill();
        if (r.y <= r.ty) {
          burst(r.x, r.y);
          rockets.splice(i, 1);
        }
      }

      // ---- particles: drawn as fading streak lines ----
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];

        // remember previous positions for the streak
        p.t.push(p.x, p.y);
        if (p.t.length > TRAIL * 2) p.t.splice(0, 2);

        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += p.g * dt;
        const drag = Math.pow(0.98, dt);
        p.vx *= drag;
        p.vy *= drag;
        p.life -= p.decay * dt;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }

        const n = p.t.length / 2;
        const base = Math.min(1, p.life * 1.3) * p.a;
        ctx.strokeStyle = p.c;
        ctx.lineCap = "round";
        ctx.lineWidth = p.w;

        // trail segments, fading toward the tail
        let px = p.t[0],
          py = p.t[1];
        for (let k = 1; k < n; k++) {
          const nx = p.t[k * 2],
            ny = p.t[k * 2 + 1];
          ctx.globalAlpha = base * (k / n);
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(nx, ny);
          ctx.stroke();
          px = nx;
          py = ny;
        }
        // final segment to the current head
        ctx.globalAlpha = base;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();

        // tiny bright head
        ctx.fillStyle = "#fff";
        ctx.globalAlpha = base * 0.9;
        ctx.fillRect(p.x - 0.6, p.y - 0.6, 1.2, 1.2);
      }
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(frame);

    const onVis = () => {
      paused = document.hidden;
      if (!paused) {
        last = performance.now();
        nextLaunch = last + 1500;
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      paused = true;
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <div className="dw-home-layer" aria-hidden="true">
      <canvas ref={canvasRef} className="dw-canvas" />
    </div>
  );
}